import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckoutPayment } from "@/components/Checkout/Payment/CheckoutPayment";
import { LoadingFragment } from "@/components/LoadingFragment";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import useCheckoutStore from "../../store/checkout.store";
import useCoupon from "./hooks/useCoupon";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn, numberFormatter } from "@/lib/utils";
import { ConfirmDialog } from "@bookips/solvook-ui-library";
import useSnackbar from "src/store/useSnackbar";
import { shallow } from "zustand/shallow";
import { BoxContainerStyle, CheckoutLayoutStyle } from ".";
import useCheckMyPass from "../Package/SubscribeToPass/useCheckMyPass";
import CashPointCard from "./CashPointCard";
import { useBillingReturn } from "./hooks/useBillingReturn";
import useSubscriptionEntryGuard from "./hooks/useSubscriptionEntryGuard";
import { useCheckoutDropDown } from "./hooks/useCheckoutDropDown";
import { useCheckoutParams } from "./hooks/useCheckoutParams";
import useCheckPurchaseOrder from "./hooks/useCheckPurchaseOrder";
import { useCheckoutSubmit } from "./hooks/useCheckoutSubmit";
import usePayment from "./hooks/usePayment";
import { useSubscriptionBilling } from "./hooks/useSubscriptionBilling";
import { useUpgradeOrderGuard } from "./hooks/useUpgradeOrderGuard";
import { NotFoundItemError } from "./NotFoundItemError";
import { DeliveryAddressSection, DeliveryUserInfoSection } from "./orderType/DeliveryOrderSection";
import OrderSection from "./orderType/OrderSection";
import { CheckoutPaymentMethodSection } from "./Payment/CheckoutPaymentMethodSection";
import usePaymentQuery from "./queries/usePaymentQuery";
import { getMixpanelOrderType, getTrackedProduct } from "./utils/checkoutTracking";
import { WaitingSpinner } from "./WaitingSpinner";
/**
 * 주문 결제 페이지
 *
 * 결제 경로가 두 개다.
 *  - 일반 결제: 토스 결제위젯을 #payment-method에 마운트한다 (usePayment)
 *  - 월구독:    위젯 대신 결제수단·약관 동의 UI를 직접 렌더한다 (orderType=subscription)
 *
 * orderType(pass·package·pod·delivery·cash 등)에 따라 조회 API와 노출 섹션이 갈린다.
 * 금액은 checkout.store가 소유하며, 쿠폰·캐시·포인트를 모두 적용한 결과가 priceInfo.finalPrice다.
 *
 * 아래 훅 호출은 결제가 진행되는 순서대로 배치했다.
 * 무엇을 → 얼마에 → 어떻게 결제하는가.
 */ export const CheckoutView = ()=>{
    const router = useRouter();
    const { addSnackbar  } = useSnackbar();
    /* ── 1. 무엇을 결제하는가 (라우터 입력) ───────────────────────── */ const { id , orderType , isBilling , isMembership , shouldUseForm , isDeliveryOrder  } = useCheckoutParams();
    const { isLoggedIn  } = useAuthStore();
    /* ── 2. 결제 정보 조회 (서버 상태) ──────────────────────────── */ const { data: paymentData , isLoading: paymentLoading , isError: isPaymentError  } = usePaymentQuery({
        orderType,
        orderId: id
    });
    // paymentData에서 뽑는 파생값. orderType별로 응답 스키마가 달라 in 연산자로 좁힌다.
    const hasMembershipPass = paymentData && "category" in paymentData;
    // 이용권·패키지 주문의 카테고리. 주문 생성 시 BE orderType 판별에 쓰인다.
    const paymentCategory = hasMembershipPass ? paymentData.category : null;
    // 패키지는 상품마다 지정된 토스 결제창(variant)을 쓴다.
    const variantKey = paymentData && "variantKey" in paymentData ? paymentData.variantKey : null;
    // 월구독 plan 응답. 진입 가드와 금액 파생의 근거다.
    const subscriptionPlan = paymentData && "alreadySubscribed" in paymentData ? paymentData : undefined;
    /* ── 3. 진입 가능한 주문인가 (가드) ─────────────────────────── */ /* 아래 셋은 조건에 걸리면 다이얼로그를 띄우고 페이지를 벗어나게 한다. */ // 판매중지·특정회원 전용 상품 차단 (pass·package만 대상)
    useCheckPurchaseOrder({
        id,
        orderType: orderType,
        paymentData
    });
    // 유효하지 않은 주문·제안 금액 만료 차단 (upgrade만 대상)
    useUpgradeOrderGuard({
        orderType,
        paymentData,
        paymentLoading
    });
    // 쏠북플러스 이용권을 이미 가진 유저 차단 (errorCode는 하단 ConfirmDialog에서 사용)
    const { errorCode , errorMessages  } = useCheckMyPass();
    // 이미 구독 중·판매 종료·구매 불가 차단 (subscription만 대상)
    useSubscriptionEntryGuard({
        enabled: isBilling,
        plan: subscriptionPlan
    });
    /* ── 4. 얼마를 결제하는가 (금액 상태) ───────────────────────── */ // 금액 계산의 단일 출처. 쿠폰·캐시·포인트 적용 결과가 priceInfo에 모인다.
    const { orderItems , userCoupons , priceInfo , deliveryInfo , updateDeliveryFee  } = useCheckoutStore((state)=>{
        return {
            orderItems: state.orderItems,
            userCoupons: state.userCoupons,
            priceInfo: state.priceInfo,
            deliveryInfo: state.deliveryInfo,
            updateDeliveryFee: state.updateDeliveryFee
        };
    }, shallow);
    const { hasCoupon , applyCoupon , checkAvailableCouponList , appliedMaxCoupon  } = useCoupon();
    /**
   * 결제 CTA 문구 (월구독 시안 3203:174159 · 3203:174122)
   * 월구독은 체험이라 청구액이 0원이면 '결제하기', 실제 청구가 있으면 '구독 및 결제하기'로 갈린다.
   *
   * 일반 결제도 여기서 만든다 — CheckoutPayment 의 폴백과 같은 문구이고,
   * mixpanel 'Button Name'(MX-3)이 화면에 찍힌 문구와 어긋나지 않으려면 출처가 하나여야 한다.
   */ const submitLabel = isBilling ? "".concat(numberFormatter(priceInfo.finalPrice), "원 ").concat(priceInfo.finalPrice > 0 ? "구독 및 " : "", "결제하기") : "".concat(numberFormatter(priceInfo.finalPrice), "원 결제하기");
    /* ── 5. 어떻게 결제하는가 (결제 수단) ───────────────────────── */ // 일반 결제. 토스 위젯 마운트와 결제창 복귀 후 완료 페이지 이동까지 담당한다.
    const { ready , mount , paymentHandle , payRequestLoading  } = usePayment({
        paymentData,
        paymentLoading,
        hasCoupon,
        variantKey,
        buttonName: submitLabel,
        onError: (error)=>{
            addSnackbar({
                message: error.message,
                variant: "error"
            });
        }
    });
    // 월구독. isBilling일 때만 쓰이지만 훅은 조건부 호출이 불가하므로 항상 호출한다.
    const billing = useSubscriptionBilling();
    // 토스 빌링 등록창에서 돌아왔을 때의 returnURL 처리
    // 복귀 후 빌링키 발급 → 가입까지 이어서 호출한다.
    const { isSubscribing  } = useBillingReturn({
        enabled: isBilling,
        planId: id,
        planTitle: subscriptionPlan === null || subscriptionPlan === void 0 ? void 0 : subscriptionPlan.title,
        onError: (error)=>{
            addSnackbar({
                message: error.message,
                variant: "error"
            });
        }
    });
    // 결제 CTA를 누르면 일어나는 일. 위 두 경로 중 하나로 분기한다.
    const { submitPayment , isPaymentEnabled  } = useCheckoutSubmit({
        id,
        isBilling,
        shouldUseForm,
        finalPrice: priceInfo.finalPrice,
        paymentCategory,
        ready,
        paymentHandle,
        requestBilling: billing.requestBilling
    });
    /* ── 6. UI 상태 ──────────────────────────────────────────── */ const { dropDown , handleDropDown  } = useCheckoutDropDown(orderItems);
    /**
   * MX-3 — 결제 CTA 클릭 시.
   *
   * 월구독만 여기서 쏜다. 위젯 결제 경로는 usePayment 안에서 이미 발사하고 있고,
   * 그쪽은 도서 주문(BooksOrder)도 함께 쓰므로 한쪽으로 합치면 커버리지가 줄어든다.
   * 약관 동의·결제수단은 빌링 UI 에만 있는 값이라 위젯 경로에서는 보낼 수 없다.
   */ const handleSubmitPayment = (e)=>{
        if (isBilling && paymentData) {
            const { title , price  } = getTrackedProduct(paymentData);
            var _subscriptionPlan_trialApplied;
            mixpanelEvent("Purchase Button Clicked", {
                "Product Categories": paymentCategory !== null && paymentCategory !== void 0 ? paymentCategory : getMixpanelOrderType(orderType),
                "Product ID": id,
                "Product Title": title,
                "Product Price": price,
                "Is Terms Agreed": billing.isAllAgreed,
                "Button Name": submitLabel,
                "Payment Method": billing.method === "CARD" ? "card" : "quick-account",
                "Order Type": getMixpanelOrderType(orderType),
                "Is Free Trial": (_subscriptionPlan_trialApplied = subscriptionPlan === null || subscriptionPlan === void 0 ? void 0 : subscriptionPlan.trialApplied) !== null && _subscriptionPlan_trialApplied !== void 0 ? _subscriptionPlan_trialApplied : false
            });
        }
        submitPayment(e);
    };
    /* ── 7. 부수효과 ─────────────────────────────────────────── */ // MX-7 — 주문/결제 페이지 진입 시. 상품 정보가 채워진 뒤 한 번만 발사한다
    useEffect(()=>{
        if (!paymentData) return;
        const { title , price  } = getTrackedProduct(paymentData);
        var _subscriptionPlan_trialApplied;
        mixpanelEvent("Order Page Viewed", {
            "Product Categories": paymentCategory !== null && paymentCategory !== void 0 ? paymentCategory : getMixpanelOrderType(orderType),
            "Product Id": id,
            "Product Title": title,
            "Product Price": price,
            "Order Type": getMixpanelOrderType(orderType),
            "Is Free Trial": (_subscriptionPlan_trialApplied = subscriptionPlan === null || subscriptionPlan === void 0 ? void 0 : subscriptionPlan.trialApplied) !== null && _subscriptionPlan_trialApplied !== void 0 ? _subscriptionPlan_trialApplied : false
        });
    // 상품 단위로 1회만 — priceInfo 변동(쿠폰·캐시)에 재발사되면 안 된다
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        paymentData
    ]);
    // 배송 주문이 아니면 배송비를 0으로 되돌린다. (주문 타입 전환 시 이전 배송비 잔존 방지)
    useEffect(()=>{
        if (orderType !== "pod" && orderType !== "delivery") {
            updateDeliveryFee(0, 0);
        }
    }, [
        orderType
    ]);
    // 페이지 이탈 시 결제 스토어 초기화
    // 재진입 시 이전 금액(stale finalPrice)으로 토스 위젯이 렌더되어
    // 가상계좌 노출 조건(10,000원)이 틀어지는 문제 방지
    useEffect(()=>{
        return ()=>{
            useCheckoutStore.getState().resetStore();
        };
    }, []);
    return /*#__PURE__*/ _jsxs(CheckoutLayoutStyle, {
        className: "max-sm:py-3",
        children: [
            (payRequestLoading || isSubscribing) && /*#__PURE__*/ _jsx(WaitingSpinner, {}),
            /*#__PURE__*/ _jsxs(BoxContainerStyle, {
                className: cn("gap-5 max-lg:max-w-[720px] max-sm:gap-3"),
                children: [
                    /*#__PURE__*/ _jsx("section", {
                        className: cn("flex w-full flex-col gap-5 max-sm:w-full", "max-sm:gap-3"),
                        children: paymentLoading || !isLoggedIn ? /*#__PURE__*/ _jsx(LoadingFragment, {}) : isPaymentError ? /*#__PURE__*/ _jsx(NotFoundItemError, {
                            handleRouting: ()=>router.push("/")
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                isDeliveryOrder && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(DeliveryUserInfoSection, {}),
                                        /*#__PURE__*/ _jsx(DeliveryAddressSection, {
                                            enabled: isLoggedIn,
                                            orderType: orderType,
                                            deliveryFeeCouponId: deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.deliveryFeeCouponId
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(OrderSection, {
                                    orderType: orderType,
                                    paymentData: paymentData,
                                    context: {
                                        orderItems,
                                        dropDown,
                                        appliedMaxCoupon,
                                        applyCoupon,
                                        userCoupons,
                                        hasCoupon,
                                        checkAvailableCouponList,
                                        handleDropDown
                                    }
                                }),
                                orderType !== "cash" && !isBilling && /*#__PURE__*/ _jsx(CashPointCard, {
                                    orderType: orderType
                                }),
                                /*#__PURE__*/ _jsx(CheckoutPaymentMethodSection, {
                                    isBilling: isBilling,
                                    finalPrice: priceInfo.finalPrice,
                                    mount: mount,
                                    billing: billing
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("section", {
                        className: "min-w-[350px] shrink-0 max-sm:w-full",
                        children: /*#__PURE__*/ _jsx(CheckoutPayment, {
                            paymentInfo: {
                                amount: priceInfo.originalPrice,
                                total: priceInfo.finalPrice,
                                discountedTotal: priceInfo.finalPrice,
                                savedPointAmount: priceInfo.savedPoint
                            },
                            deliveryFee: {
                                basicDeliveryCharge: (deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.basicDeliveryCharge) || 0,
                                remoteAreaFee: (deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.remoteAreaFee) || 0
                            },
                            discount: {
                                // 합계 행은 base + 쿠폰이고 하위 행은 쿠폰만 보여야 해서 두 값을 나눠 넘긴다
                                baseDiscountAmount: priceInfo.baseProductDiscount || 0,
                                discountedAmount: priceInfo.couponDiscount || 0,
                                deliveryFeeCouponAmount: (deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.deliveryFeeCouponAmount) || 0
                            },
                            paymentMethod: {
                                usedCashAmount: priceInfo.usedCash || 0,
                                usedPointAmount: priceInfo.usedPoint || 0
                            },
                            paymentHandle: handleSubmitPayment,
                            paymentReady: isPaymentEnabled,
                            submitLabel: submitLabel
                        })
                    })
                ]
            }),
            isMembership && errorCode && errorCode !== 200 && /*#__PURE__*/ _jsx(ConfirmDialog, {
                open: true,
                ...errorMessages[errorCode]
            })
        ]
    });
};
